import { Alert, Button, Card, Checkbox, Field, PageHeader, useToast } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { FileText, Plus, type LucideIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { Controller, type Resolver, useForm } from 'react-hook-form';

import {
  BILLING_CYCLES,
  BILLING_CYCLE_LABELS,
  SERVICE_DAYS,
  SERVICE_DAY_LABELS,
  createContract,
  type ContractFormInput,
  type ContractStatus,
} from '../../contracts/api';
import {
  DEFAULT_FORM_VALUES,
  contractFormSchema,
  mapContractError,
  toCreateInput,
  type ContractFormValues,
  type FieldErrorTarget,
} from '../../contracts/contract-form-schema';
import { searchAllCustomers, type CustomerListItem } from '../../customers/api';
import { FormSection } from '../../components/form-section';
import { OperatingUnitSelect } from '../../components/operating-unit-select';

const contractNewRoute = getRouteApi('/protected/contracts/new');

/**
 * `exactOptionalPropertyTypes` treats an explicit `error={undefined}` as a
 * different (rejected) shape than the prop being absent; this omits the key
 * entirely instead of ever assigning `undefined` to it.
 */
function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

export interface ContractFormProps {
  mode: 'create' | 'edit';
  customers: CustomerListItem[];
  defaultValues?: ContractFormValues;
  /** Only meaningful in `edit` mode: drives the versioning notice (`contracts/spec.md` → "Edición de contrato activo genera nueva versión"). */
  contractStatus?: ContractStatus;
  submitLabel: string;
  /** Decorative: aria-hidden, same convention as `Button`'s own `icon` prop. */
  submitIcon?: LucideIcon;
  onSubmit: (values: ContractFormValues) => Promise<void>;
  submitting: boolean;
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
  onCancel?: () => void;
}

/**
 * `contracts/spec.md` → "Pantallas de contrato en back-office". Used both by
 * `/contracts/new` and by `ContractConditionsTab`'s edit mode, same
 * criterion as `CustomerForm`/`MenuForm`. Editing an `ACTIVE` contract shows
 * an explicit notice that requires a checkbox confirmation before the
 * submit button actually fires `onSubmit` — the only way the user learns
 * why the version number is about to change.
 */
export function ContractForm({
  mode,
  customers,
  defaultValues,
  contractStatus,
  submitLabel,
  submitIcon,
  onSubmit,
  submitting,
  formError,
  fieldError,
  onCancel,
}: ContractFormProps): JSX.Element {
  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ContractFormValues, unknown, ContractFormValues>({
    resolver: zodResolver(contractFormSchema) as unknown as Resolver<
      ContractFormValues,
      unknown,
      ContractFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_FORM_VALUES,
  });

  const requiresVersioningConfirmation = mode === 'edit' && contractStatus === 'ACTIVE';
  const [versioningConfirmed, setVersioningConfirmed] = useState(false);

  useEffect(() => {
    if (fieldError !== undefined) {
      setError(fieldError.field, { type: 'server', message: fieldError.message });
    }
  }, [fieldError, setError]);

  function handleValidSubmit(values: ContractFormValues): void {
    if (requiresVersioningConfirmation && !versioningConfirmed) {
      return;
    }

    void onSubmit(values);
  }

  return (
    <form
      onSubmit={(event) => void handleSubmit(handleValidSubmit)(event)}
      className="flex flex-col gap-6"
      noValidate
    >
      {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

      {requiresVersioningConfirmation ? (
        <Alert tone="warning" title="Esta edición genera una nueva versión">
          <Checkbox
            label="Confirmo que quiero crear una nueva versión del contrato con estos cambios."
            checked={versioningConfirmed}
            onChange={(event) => setVersioningConfirmed(event.target.checked)}
          />
        </Alert>
      ) : null}

      <FormSection title="Identificación">
        <Field
          label="Código"
          required
          {...register('code')}
          {...maybeError(errors.code?.message)}
          disabled={mode === 'edit'}
        />
        <SelectField
          label="Cliente"
          {...register('customerId')}
          {...maybeError(errors.customerId?.message)}
          options={customers.map((customer) => ({ value: customer.id, label: customer.legalName }))}
          disabled={mode === 'edit'}
        />
        <Controller
          control={control}
          name="operatingUnitId"
          render={({ field }) => (
            <OperatingUnitSelect
              id="contract-form-operatingUnitId"
              label="Punto de venta"
              required
              // `design.md` §9: the point is immutable once the contract leaves `DRAFT`.
              disabled={mode === 'edit' && contractStatus !== 'DRAFT'}
              value={field.value}
              onChange={field.onChange}
              error={errors.operatingUnitId?.message}
            />
          )}
        />
      </FormSection>

      <FormSection title="Vigencia y facturación">
        <Field
          label="Fecha de inicio"
          type="date"
          required
          {...register('startDate')}
          {...maybeError(errors.startDate?.message)}
          disabled={mode === 'edit'}
        />
        <Field
          label="Fecha de fin"
          type="date"
          {...register('endDate')}
          {...maybeError(errors.endDate?.message)}
        />
        <SelectField
          label="Ciclo de facturación"
          {...register('billingCycle')}
          {...maybeError(errors.billingCycle?.message)}
          options={BILLING_CYCLES.map((value) => ({ value, label: BILLING_CYCLE_LABELS[value] }))}
        />
        <Field
          label="Plazo de pago (días)"
          required
          {...register('paymentTermDays')}
          {...maybeError(errors.paymentTermDays?.message)}
        />
      </FormSection>

      <FormSection title="Corte y cupos">
        <Field
          label="Hora de corte propia"
          type="time"
          hint="Si lo dejás vacío, se usa la hora de corte del punto de venta."
          {...register('cutoffTime')}
          {...maybeError(errors.cutoffTime?.message)}
        />
        <Field
          label="Días de anticipación de corte"
          type="number"
          min={0}
          hint="Si lo dejás vacío, se usan los días del punto de venta."
          {...register('cutoffDaysBefore')}
          {...maybeError(errors.cutoffDaysBefore?.message)}
        />
        <Field
          label="Tope mínimo de cantidad diaria"
          type="number"
          min={0}
          {...register('minDailyQuantity')}
          {...maybeError(errors.minDailyQuantity?.message)}
        />
        <Field
          label="Tope máximo de cantidad diaria"
          type="number"
          min={0}
          {...register('maxDailyQuantity')}
          {...maybeError(errors.maxDailyQuantity?.message)}
        />
        <div className="sm:col-span-2">
          <Checkbox
            label="Permite exceder la hora de corte"
            hint="Si lo marcás, se pueden cargar pedidos aunque ya haya pasado la hora de corte."
            {...register('allowsCutoffOverride')}
          />
        </div>
      </FormSection>

      <FormSection title="Servicio">
        <div className="sm:col-span-2">
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-slate-900">Días de servicio activos</legend>
            {/* Controlled rather than `register`ed: a native checkbox group
                reports string values ("1"), while the API and the schema use
                ISO weekday numbers, and registered defaults would never show
                as checked on edit. */}
            <Controller
              control={control}
              name="serviceDays"
              render={({ field }) => (
                <div className="flex flex-wrap gap-3">
                  {SERVICE_DAYS.map((day) => (
                    <Checkbox
                      key={day}
                      label={SERVICE_DAY_LABELS[day]}
                      name={field.name}
                      checked={field.value.includes(day)}
                      onBlur={field.onBlur}
                      onChange={(event) =>
                        field.onChange(
                          event.target.checked
                            ? [...field.value, day].sort((left, right) => left - right)
                            : field.value.filter((value) => value !== day),
                        )
                      }
                    />
                  ))}
                </div>
              )}
            />
          </fieldset>
        </div>
        <Checkbox label="Incluye feriados" {...register('includesHolidays')} />
      </FormSection>

      <FormSection title="Notas">
        <div className="sm:col-span-2">
          <Field label="Notas" {...register('notes')} {...maybeError(errors.notes?.message)} />
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-3">
        {onCancel === undefined ? null : (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          {...(submitIcon === undefined ? {} : { icon: submitIcon })}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  error?: string | undefined;
  options: SelectFieldOption[];
  disabled?: boolean;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur: FocusEventHandler<HTMLSelectElement>;
}

/**
 * No shared `Select` component yet (`design.md` §7); mirrors
 * `CustomerForm`'s `SelectField`, including the `forwardRef` requirement
 * `register()` needs to actually update the value.
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, name, error, options, disabled, onChange, onBlur },
  ref,
) {
  const selectId = `contract-form-${name}`;
  const messageId = `${selectId}-message`;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-slate-900">
        {label}
        <span aria-hidden="true" className="ml-0.5 text-red-600">
          *
        </span>
      </label>
      <select
        id={selectId}
        name={name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        className={`h-11 rounded-md border px-3 text-sm ${hasError ? 'border-red-500' : 'border-slate-300'}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? messageId : undefined}
      >
        <option value="">Seleccioná una opción</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <p id={messageId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});

/** `contracts/spec.md` → "Alta de contrato en borrador", "Pantallas de contrato en back-office". */
export function ContractFormPage(): JSX.Element {
  const navigate = contractNewRoute.useNavigate();
  const { customerId } = contractNewRoute.useSearch();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const customersQuery = useQuery({
    queryKey: ['customers-for-contracts'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  const mutation = useMutation({
    mutationFn: (input: ContractFormInput) => createContract(input),
    onSuccess: async (contract) => {
      await queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast('success', 'Contrato creado');
      await navigate({ to: '/contracts/$contractId', params: { contractId: contract.id } });
    },
  });

  const { formError, fieldError } = mapContractError(mutation.error);

  async function handleSubmit(values: ContractFormValues): Promise<void> {
    // `mutation.error` (read via `mapContractError` above) is the source of
    // truth for the UI; the rejection itself would otherwise be an
    // unhandled promise.
    await mutation.mutateAsync(toCreateInput(values)).catch(() => undefined);
  }

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        icon={FileText}
        title="Nuevo contrato"
        description="Definí las condiciones comerciales y de servicio del nuevo contrato."
      />
      <Card>
        {customersQuery.data === undefined ? (
          <p className="text-sm text-slate-500">Cargando...</p>
        ) : (
          /**
           * FINDINGS.md → segunda corrida: `useForm`'s `defaultValues` are
           * only read once, at mount. Mounting `ContractForm` before
           * `customersQuery` resolves left the preselected `customerId`
           * with no matching `<option>` yet, so the native `<select>` fell
           * back to the empty placeholder even once the options arrived.
           * Waiting for the customers to load before mounting the form
           * guarantees the option exists on the same render that sets it.
           */
          <ContractForm
            mode="create"
            customers={customersQuery.data.items}
            {...(customerId === undefined
              ? {}
              : { defaultValues: { ...DEFAULT_FORM_VALUES, customerId } })}
            submitLabel="Crear contrato"
            submitIcon={Plus}
            submitting={mutation.isPending}
            onSubmit={handleSubmit}
            {...(formError === undefined ? {} : { formError })}
            {...(fieldError === undefined ? {} : { fieldError })}
          />
        )}
      </Card>
    </section>
  );
}
